import { useGlobalAuthStore } from '@stores/bootstrap-stores/auth';
import { useAppUserStore } from '@stores/bootstrap-stores/user';
import { saveReturnUrl } from '@utils/auth/returnUrl';
import { useLocation } from 'react-router-dom';
import { type AppRouteConfig } from '../route';
import { canActivateRoute } from './routeGuard';

type AccessDecision = {
  canAccess: boolean;
  redirectTo?: string;
};

export function useAuthGuard(route: AppRouteConfig): AccessDecision {
  const location = useLocation();
  const token = useGlobalAuthStore((state) => state.token);
  const authUser = useGlobalAuthStore((state) => state.authUser);
  const isAuthResolved = useGlobalAuthStore((state) => state.isAuthResolved);
  const isUserContextInitialized = useAppUserStore((state) => state.isInitialized);

  if (route.requiresAuth && !isAuthResolved) {
    return { canAccess: true };
  }

  const isAuthenticated = Boolean(token || authUser);

  if (route.requiresAuth && isAuthenticated && route.resourceId && !isUserContextInitialized) {
    return { canAccess: true };
  }

  const decision = canActivateRoute({
    requiresAuth: route.requiresAuth,
    isAuthenticated,
    resourceId: route.resourceId,
    redirectUnauthorizedTo: route.redirectUnauthorizedTo
  });

  if (!decision.canAccess && decision.redirectTo === '/__/auth') {
    const returnUrl = `${location.pathname}${location.search}`;

    if (returnUrl && returnUrl !== '/' && !returnUrl.startsWith('/__/auth')) {
      saveReturnUrl(returnUrl, authUser?.uid ?? null);
    }
  }

  return decision;
}
